import { readCookie } from "common/utils/cookie";
import { getUserId, getOrderNumber } from "common/utils/user-context";

/**
 * UserContextHelper
 *
 * Centralized helper for extracting user context and location data
 * for local delivery tracking and address operations.
 * Maintains a single source of truth using cookies.
 */
class UserContextHelper {
  /**
   * Gets user authentication and identification context
   *
   * @returns {{isLoggedIn: boolean, orderNumber: string|null, userId: number|null}} User context
   * @public
   */
  static getUserContext() {
    const isLoggedIn = Boolean(readCookie("name"));
    const orderNumber = getOrderNumber();
    const userId = getUserId();

    return {
      isLoggedIn,
      orderNumber,
      userId
    };
  }

  /**
   * Gets the session zipcode from cookies
   *
   * @returns {string} The zipcode from address cookie or empty string
   * @public
   */
  static getSessionZipcode() {
    const address = readCookie("address");
    if (!address) return "";

    // Parse zipcode from address cookie (format: "StreetNumber+Street+City+State+Zipcode")
    // Get all digit-only parts and take the last one (zipcode, not street number)
    const parts = address.split("+");
    const digitParts = parts.filter(part => /^\d+$/.test(part));
    const zipcodePart = digitParts[digitParts.length - 1];

    return zipcodePart || "";
  }

  /**
   * Gets user type for tracking purposes
   *
   * @returns {"guest" | "logged_in" | "logged_in_no_address"} User type
   * @public
   */
  static getUserType() {
    const { isLoggedIn } = this.getUserContext();
    return isLoggedIn ? "logged_in" : "guest";
  }

  /**
   * Gets user type for modal display (distinguishes logged in users without address)
   *
   * @returns {"guest" | "logged_in_no_address"} User type for modal
   * @public
   */
  static getModalUserType() {
    const { isLoggedIn } = this.getUserContext();
    return isLoggedIn ? "logged_in_no_address" : "guest";
  }
}

export default UserContextHelper;
